import React, { memo, useEffect } from 'react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Close } from '@mui/icons-material';

import { useTranslation } from 'react-i18next';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  modalNumber: number;
  classname?: string;
  content: React.ReactNode;
  footer: React.ReactNode;
  onlyConfirm?: boolean;

  footerClassName?: string;
  mainClassName?: string;
}

export const Modal = memo(
  ({
    isOpen,
    onClose,
    title,
    modalNumber = 1,
    classname,
    content,
    footer,
    onlyConfirm = false,

    footerClassName,
    mainClassName,
  }: Props) => {
    const { t } = useTranslation();
    const handleClose = () => {
      onClose();
    };

    const handleOverlayClick = () => {
      if (!onlyConfirm) {
        handleClose();
      }
    };

    useEffect(() => {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          handleClose();
        }
      });

      return () => {
        document.removeEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            handleClose();
          }
        });
      };
    }, []);

    if (!isOpen) return null;

    const zIndex = 1000 + modalNumber;

    return (
      <>
        <div
          className="fixed inset-0 flex items-center justify-center modal-show"
          onClick={handleOverlayClick}
          style={{
            zIndex: zIndex,
          }}
        />
        <div
          className={clsx(
            twMerge(
              'fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 min-w-[98%] md:min-w-[35rem] min-h-40 bg-white opacity-1 rounded-xl flex flex-col justify-between items-center p-1 md:p-4 shadow-2xl',
              classname
            )
          )}
          style={{
            zIndex: zIndex + 1,
          }}>
          <header className="flex items-center justify-between w-full gap-2 p-4 h-14 rounded-t-md">
            <h1 className="text-xl font-bold text-primary">{t(title)}</h1>
            {!onlyConfirm && <Close onClick={handleClose} className="text-xl cursor-pointer" />}
          </header>
          <main className={clsx('w-full max-h-[75vh] overflow-y-auto overflow-x-hidden py-5 md:px-5', mainClassName)}>{content}</main>
          <footer className={clsx('w-full pt-4', footerClassName)}>{footer}</footer>
        </div>
      </>
    );
  }
);
